import React, { useState } from 'react';
import { 
  Card, CardContent, CardMedia, Typography, Chip, Box, IconButton, 
  Popover, List, ListItem, ListItemIcon, ListItemText, Divider, Tooltip
} from '@mui/material';
import { 
  Bookmark, BookmarkBorder, Visibility, VisibilityOff, Info, 
  Share as ShareIcon, X as XIcon
} from '@mui/icons-material';
import DiscordIcon from './icons/DiscordIcon';

const GameCard = ({ 
  game, 
  selected, 
  onSelect, 
  onMoreDetails, 
  onToggleVisibility, 
  onToggleWatchlist 
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleSocialClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleSocialClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const hasSocialLinks = game.links && (game.links.twitter || game.links.discord);

  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        position: 'relative',
        outline: selected ? '2px solid #f4364c' : 'none',
        outlineOffset: '-2px',
        opacity: game.hidden ? 0.6 : 1,
        '&:hover': {
          boxShadow: (theme) => theme.shadows[8],
        },
      }}
      onClick={onSelect}
    >
      <Box 
        sx={{ 
          position: 'absolute', 
          top: 8, 
          right: 8, 
          zIndex: 1,
          display: 'flex',
          borderRadius: '16px',
          overflow: 'hidden',
          bgcolor: 'rgba(0, 0, 0, 0.6)',
        }}
      >
        <Tooltip title={game.inWatchlist ? "Remove from Watch List" : "Add to Watch List"}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onToggleWatchlist();
          }}
          sx={{ 
            color: 'white',
            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' } 
          }}
        >
          {game.inWatchlist ? <Bookmark /> : <BookmarkBorder />}
        </IconButton>
        </Tooltip>
        <Tooltip title={game.hidden ? "Unhide" : "Hide"}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onToggleVisibility();
          }}
          sx={{ 
            color: 'white',
            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' } 
          }}
        >
          {game.hidden ? <Visibility /> : <VisibilityOff />}
        </IconButton>
        </Tooltip>
      </Box>
      <CardMedia
        component="img"
        image={`https://cdn.akamai.steamstatic.com/steam/apps/${game.appId}/header.jpg`}
        alt={game.name}
        height="140"
      />
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', pb: 0 }}>
        <Typography variant="h6" component="div" noWrap>
          {game.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Confidence Rating: {Math.ceil(game.confidenceRating)}
        </Typography>
        <Box sx={{ mb: 2 }}>
          {game.tags.slice(0, 5).map((tag) => (
            <Chip key={tag} label={tag} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
          ))}
          {game.categories.slice(0, 1).map((category) => (
            <Chip key={category} label={category} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
          ))}
        </Box>
      </CardContent>
      <Divider />
      <Box sx={{ display: 'flex', justifyContent: 'space-around', py: 1 }}>
        <Tooltip title="Links">
        <IconButton
          size="small"
          onClick={handleSocialClick}
          disabled={!hasSocialLinks}
          color="primary"
        >
          <ShareIcon fontSize="small" />
        </IconButton>
        </Tooltip>
        <Tooltip title="More Details">
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onMoreDetails();
          }}
          color="primary"
        >
          <Info fontSize="small" />
        </IconButton>
        </Tooltip>
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleSocialClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <List>
          {game.links && game.links.discord && (
            <ListItem button component="a" href={game.links.discord} target="_blank" rel="noopener noreferrer">
              <ListItemIcon>
                <DiscordIcon />
              </ListItemIcon>
              <ListItemText primary="Discord" />
            </ListItem>
          )}
          {game.links && game.links.x && (
            <ListItem button component="a" href={game.links.x} target="_blank" rel="noopener noreferrer">
              <ListItemIcon>
                <XIcon />
              </ListItemIcon>
              <ListItemText primary="Twitter" />
            </ListItem>
          )}
        </List>
      </Popover>
    </Card>
  );
};

export default GameCard;